import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import PDFDetailedPages from "./PDFDetailedPages";
import {
  formatCurrency,
  formatDate,
  calculateInstallments,
  resolveInstallmentInfo,
} from "@/lib/formatters";
import { useOrderCalculations } from "@/hooks/useOrderCalculations";
import { getDeliveryTypeTotalUtil, isProgrammedOrderItem, belongsToOrderMode, showsProgrammedListValue } from "@/lib/utils";
import { getSecondSemesterAdjustmentDeadline } from "@/lib/orderCycle";

interface OrderItem {
  id: number;
  codigo_produto: string;
  descricao_produto: string;
  preco: number;
  ano_letivo: number;
  marca: string;
  sku: string;
  status: string;
  utilizacao?: string | null;
  serie?: string | null;
  segmento?: string | null;
  quantidade_contratual: number;
  desconto: number;
  quantidade_pedido: number;
  valor_liquido: number;
  valor_total: number;
  delivery_type_id: number;
  delivery_type_number: number;
  brand_id: number;
  scheduling_category?: string;
}

interface PDFSummaryProps {
  orderNumber: string;
  clientName: string;
  clientCode: string;
  orderItems: OrderItem[];
  grossSubtotal: number;
  netSubtotal: number;
  total: number;
  shippingTotal: number;
  date?: string;
  isScheduled: boolean;
  annualQuantityInstallment?: number;
  semestralQuantityInstallment?: number;
  quantityInstallment?: number;
  cycleYear?: number;
}

const PDFSummary = ({
  orderNumber,
  clientName,
  clientCode,
  orderItems,
  grossSubtotal,
  netSubtotal,
  total,
  shippingTotal,
  isScheduled,
  date,
  annualQuantityInstallment,
  semestralQuantityInstallment,
  quantityInstallment,
  cycleYear,
}: PDFSummaryProps) => {
  const adjustmentDeadline = formatDate(getSecondSemesterAdjustmentDeadline(cycleYear ?? new Date().getFullYear()));
  const getSafeDate = () => {
    const d = new Date();
    if (isNaN(d.getTime()) || d.getFullYear() < 2020) {
      return new Date(Date.now());
    }
    return d;
  };

  const safeNow = getSafeDate();
  const day = String(safeNow.getDate()).padStart(2, '0');
  const month = String(safeNow.getMonth() + 1).padStart(2, '0');
  const year = safeNow.getFullYear();
  const hours = String(safeNow.getHours()).padStart(2, '0');
  const minutes = String(safeNow.getMinutes()).padStart(2, '0');
  const seconds = String(safeNow.getSeconds()).padStart(2, '0');

  const formattedDate = `${day}/${month}/${year}`;
  const formattedTime = `${hours}:${minutes}:${seconds}`;

  // Group products by description AND serie to avoid incorrect aggregation
  const groupProductsByDescription = (items: OrderItem[]) => {
    const grouped = items.reduce((acc, item) => {
      // Create a composite key: description + serie to group by both
      const key = `${item.descricao_produto}__${item.serie || "sem-serie"}`;
      if (!acc[key]) {
        acc[key] = {
          ...item,
          quantidade_pedido: 0,
          valor_total: 0,
        };
      }
      acc[key].quantidade_pedido += item.quantidade_pedido;
      acc[key].valor_total += item.valor_total;
      return acc;
    }, {} as Record<string, OrderItem>);

    // Recalculate valor_liquido after grouping
    return Object.values(grouped).map((item) => ({
      ...item,
      valor_liquido:
        item.quantidade_pedido > 0
          ? item.valor_total / item.quantidade_pedido
          : 0,
    }));
  };

  // Map marca (which contains ID as string) to brand name
  const getBrandName = (marca: string): string => {
    const brandMap: Record<string, string> = {
      "1": "AZ",
      "2": "High Five",
      "3": "My Life",
      "4": "LEX",
      "5": "AI4SCHOOL",
    };
    return brandMap[marca] || marca || "Sem Marca";
  };

  // Group products by brand
  const groupProductsByBrand = (items: OrderItem[]) => {
    // First aggregate products with identical descriptions
    const aggregatedProducts: Record<string, OrderItem> = {};

    items.forEach((item) => {
      const key = JSON.stringify([
        item.marca,
        item.descricao_produto,
        item.sku ?? "",
        item.serie,
        item.utilizacao,
      ]);

      if (aggregatedProducts[key]) {
        // Aggregate quantities and totals
        aggregatedProducts[key].quantidade_pedido += item.quantidade_pedido;
        aggregatedProducts[key].valor_total += item.valor_total;
        aggregatedProducts[key].quantidade_contratual +=
          item.quantidade_contratual;
      } else {
        // Create new entry
        aggregatedProducts[key] = { ...item };
      }
    });

    const aggregatedItems = Object.values(aggregatedProducts);

    const grouped = aggregatedItems.reduce((acc, item) => {
      const brand = getBrandName(item.marca);
      if (!acc[brand]) {
        acc[brand] = [];
      }
      acc[brand].push(item);
      return acc;
    }, {} as Record<string, OrderItem[]>);

    // Sort brands by brand_id (ascending) and calculate subtotals
    return Object.entries(grouped)
      .sort(([, productsA], [, productsB]) => {
        // Use the first product's brand_id for comparison
        const brandIdA = productsA[0]?.brand_id || 0;
        const brandIdB = productsB[0]?.brand_id || 0;
        return brandIdA - brandIdB;
      })
      .map(([brandName, products]) => ({
        brandName,
        products: products.sort((a, b) => {
          // First sort by utilizacao (alunos first, then professores)
          if (a.utilizacao !== b.utilizacao) {
            const aIsTeacher = a.utilizacao === "Professor" ? 1 : 0;
            const bIsTeacher = b.utilizacao === "Professor" ? 1 : 0;
            return aIsTeacher - bIsTeacher;
          }
          // Then sort by description (alphabetical)
          return a.descricao_produto.localeCompare(b.descricao_produto);
        }),
        subtotal: products.reduce((sum, item) => sum + item.valor_total, 0),
      }));
  };

  // Get products by delivery type with proper sorting and grouping
  const getProductsByDeliveryType = (deliveryTypeNumber: number) => {
    const filtered = orderItems.filter((item) => {
      // Filter by delivery type and quantity > 0
      if (
        item.delivery_type_number !== deliveryTypeNumber ||
        item.quantidade_pedido <= 0
      ) {
        return false;
      }

      if (!belongsToOrderMode(item, isScheduled, cycleYear)) {
        return false;
      }

      // Exclude "Plataforma AZ - 2º Semestre" from semestral products
      // if (
      //   deliveryTypeNumber === 2 &&
      //   item.descricao_produto.includes( "Plataforma AZ" ) &&
      //   item.descricao_produto.includes( "2º Semestre" )
      // ) {
      //   return false;
      // }

      return true;
    });

    // Group by description and then sort
    const grouped = groupProductsByDescription(filtered);

    return grouped.sort((a, b) => {
      // First sort by brand_id (ascending)
      if (a.brand_id !== b.brand_id) {
        return a.brand_id - b.brand_id;
      }
      // Then sort by utilizacao (alunos first, then professores)
      if (a.utilizacao !== b.utilizacao) {
        const aIsTeacher = a.utilizacao === "Professor" ? 1 : 0;
        const bIsTeacher = b.utilizacao === "Professor" ? 1 : 0;
        return aIsTeacher - bIsTeacher;
      }
      // Then sort by description (alphabetical)
      return a.descricao_produto.localeCompare(b.descricao_produto);
    });
  };

  // Calculate totals by delivery type
  const getDeliveryTypeTotal = (deliveryTypeNumber: number) => {
    const itemsForCurrentDocument = orderItems.filter((item) =>
      belongsToOrderMode(item, isScheduled, cycleYear)
    );
    return getDeliveryTypeTotalUtil(itemsForCurrentDocument, deliveryTypeNumber, isScheduled);
  };

  // Get scheduled order items with brand grouping
  const getScheduledItems = () => {
    // Lista informativa do pedido programado (mesmo critério do resumo do checkout): quantidade de todos
    // os itens e valor só dos itens de 2º semestre; avaliações/simulados aparecem sem valor.
    // Nada disso entra no total do pedido atual, que usa belongsToOrderMode.
    const scheduledItems = orderItems
      .filter((item) => isProgrammedOrderItem(item, cycleYear))
      .map((item) => showsProgrammedListValue(item)
        ? item
        : { ...item, preco: 0, valor_liquido: 0, valor_total: 0 });

    let totalValue = 0;
    let totalQuantity = 0;

    scheduledItems.forEach((item) => {
      const itemValue = item.valor_liquido * item.quantidade_pedido;
      totalValue += itemValue;
      totalQuantity += item.quantidade_pedido;
    });

    // Group scheduled items by description first, then apply brand grouping
    const groupedScheduledItems = groupProductsByDescription(scheduledItems);
    const brandGroupedScheduledItems = groupProductsByBrand(
      groupedScheduledItems
    );

    return {
      hasScheduledItems: scheduledItems.length > 0,
      totalValue,
      totalQuantity,
      brandGroups: brandGroupedScheduledItems,
    };
  };

  const produtosAnuais = getProductsByDeliveryType(1);
  const produtosSemestrais = getProductsByDeliveryType(2);
  const totalProdutosAnuais = getDeliveryTypeTotal(1);
  const totalProdutosSemestrais = getDeliveryTypeTotal(2);
  const { hasScheduledItems, totalValue, totalQuantity, brandGroups } = getScheduledItems();

  // Cálculos do Plano de Parcelas com arredondamento correto

  const annualInfo = resolveInstallmentInfo(
    annualQuantityInstallment,
    quantityInstallment,
    12
  );

  const semestralInfo = resolveInstallmentInfo(
    semestralQuantityInstallment,
    totalProdutosAnuais === 0 ? quantityInstallment : undefined,
    6
  );

  const annualInstallments = calculateInstallments(totalProdutosAnuais, annualInfo.calcQtd);
  const semestralInstallments = calculateInstallments(totalProdutosSemestrais, semestralInfo.calcQtd);
  const nextYear = new Date().getFullYear();

  // Removed coupon logic - always 0
  const couponDiscountAmount = 0;

  return (
    <>
      {/* First Page - Summary */}
      <div
        className="bg-white p-8 max-w-4xl mx-auto"
        style={{ width: "210mm", minHeight: "297mm" }}
      >
        {/* Header */}
        <div className="text-center mb-8 border-b-2 border-gray-300 pb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            PEDIDO DE COMPRA
          </h1>
          <div className="text-xl font-semibold text-blue-600 mb-2">
            #{orderNumber}
          </div>
          <div className="text-gray-600">
            Gerado em: {formattedDate} às {formattedTime}
          </div>
        </div>

        {/* Client Information */}
        <Card className="mb-6 shadow-none border-gray-300">
          <CardHeader className="bg-gray-50">
            <CardTitle className="text-lg text-gray-800">
              Dados do Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="font-semibold text-gray-700">Nome:</span>
                <div className="text-gray-900">{clientName}</div>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Código CNX:</span>
                <div className="text-gray-900">{clientCode}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Order Summary */}
        <Card className="mb-6 shadow-none border-gray-300">
          <CardHeader className="bg-gray-50">
            <CardTitle className="text-lg text-gray-800">
              Resumo Financeiro
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-6">
              {/* Pedido Anual */}
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">
                  📅 Pedido Anual
                </span>
                <span className="font-bold text-lg">
                  {formatCurrency(totalProdutosAnuais)}
                </span>
              </div>

              {produtosAnuais.length > 0 && (
                <div className="ml-4 space-y-2 text-sm text-gray-600">
                  {groupProductsByBrand(produtosAnuais)
                    .slice(0, 3)
                    .map((brandGroup, brandIndex) => (
                      <div key={`brand-${brandIndex}`} className="space-y-1">
                        <div className="flex justify-between items-center py-1 px-2 bg-gray-100 rounded text-xs font-medium">
                          <span className="text-gray-700">
                            📚 {brandGroup.brandName}
                          </span>
                          <span className="text-gray-800 font-semibold">
                            {formatCurrency(brandGroup.subtotal)}
                          </span>
                        </div>
                        <div className="ml-3 space-y-0.5">
                          {brandGroup.products.slice(0, 3).map((item) => (
                            <div
                              key={item.id}
                              className="flex justify-between text-xs"
                            >
                              <span className="truncate flex-1">
                                {item.descricao_produto}
                              </span>
                              <span className="ml-2 whitespace-nowrap">
                                {formatCurrency(item.valor_total)}
                              </span>
                            </div>
                          ))}
                          {brandGroup.products.length > 3 && (
                            <div className="text-xs italic text-gray-500">
                              ... e mais {brandGroup.products.length - 3}{" "}
                              produtos
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  {groupProductsByBrand(produtosAnuais).length > 3 && (
                    <div className="text-xs italic text-gray-500">
                      ... e mais{" "}
                      {groupProductsByBrand(produtosAnuais).length - 3} marcas
                    </div>
                  )}
                </div>
              )}

              <Separator />

              {/* Pedido Semestre */}
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">
                  📆 Pedido Semestral
                </span>
                <span className="font-bold text-lg">
                  {formatCurrency(totalProdutosSemestrais)}
                </span>
              </div>

              {produtosSemestrais.length > 0 && (
                <div className="ml-4 space-y-2 text-sm text-gray-600">
                  {groupProductsByBrand(produtosSemestrais)
                    .slice(0, 3)
                    .map((brandGroup, brandIndex) => (
                      <div key={`brand-${brandIndex}`} className="space-y-1">
                        <div className="flex justify-between items-center py-1 px-2 bg-blue-100 rounded text-xs font-medium">
                          <span className="text-blue-700">
                            📚 {brandGroup.brandName}
                          </span>
                          <span className="text-blue-800 font-semibold">
                            {formatCurrency(brandGroup.subtotal)}
                          </span>
                        </div>
                        <div className="ml-3 space-y-0.5">
                          {brandGroup.products.slice(0, 3).map((item) => (
                            <div
                              key={item.id}
                              className="flex justify-between text-xs"
                            >
                              <span className="truncate flex-1">
                                {item.descricao_produto}
                              </span>
                              <span className="ml-2 whitespace-nowrap">
                                {formatCurrency(item.valor_total)}
                              </span>
                            </div>
                          ))}
                          {brandGroup.products.length > 3 && (
                            <div className="text-xs italic text-gray-500">
                              ... e mais {brandGroup.products.length - 3}{" "}
                              produtos
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  {groupProductsByBrand(produtosSemestrais).length > 3 && (
                    <div className="text-xs italic text-gray-500">
                      ... e mais{" "}
                      {groupProductsByBrand(produtosSemestrais).length - 3}{" "}
                      marcas
                    </div>
                  )}
                </div>
              )}

              {shippingTotal > 0 && (
                <>
                  <Separator />
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">
                      🚚 Frete
                    </span>
                    <span className="font-bold text-lg">
                      {formatCurrency(shippingTotal)}
                    </span>
                  </div>
                </>
              )}

              <Separator />

              <div className="flex justify-between items-center text-xl font-bold bg-blue-50 p-4 rounded-lg">
                <span className="text-blue-800">Total Geral</span>
                <span className="text-blue-800">{formatCurrency(total)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Installment Plan */}
        {(totalProdutosAnuais > 0 || totalProdutosSemestrais > 0) && (
          <Card className="mb-6 shadow-none border-gray-300">
            <CardHeader className="bg-gray-50">
              <CardTitle className="text-lg text-gray-800">
                💳 Plano de Parcelas
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-4">
                <div className="text-sm text-gray-600 mb-4">
                  Cronograma de pagamento para o ano letivo {nextYear}
                </div>

                {totalProdutosAnuais > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center bg-green-50 p-3 rounded-lg">
                      <span className="text-green-800 font-medium">
                        📅 Pedido Anual - {annualInfo.label}
                      </span>
                      <span className="text-green-800 font-bold">
                        {formatCurrency(annualInstallments.installmentValue)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 ml-4">
                      Total: {formatCurrency(totalProdutosAnuais)} {annualInfo.subLabel} de janeiro a dezembro/{nextYear}
                    </div>
                  </div>
                )}

                {totalProdutosSemestrais > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center bg-blue-50 p-3 rounded-lg">
                      <span className="text-blue-800 font-medium">
                        📆 Pedido Semestral - {semestralInfo.label}
                      </span>
                      <span className="text-blue-800 font-bold">
                        {formatCurrency(semestralInstallments.installmentValue)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 ml-4">
                      Total: {formatCurrency(totalProdutosSemestrais)} {semestralInfo.subLabel} de janeiro a junho/{nextYear}
                    </div>
                  </div>
                )}

                <div className="mt-4 p-3 bg-gray-100 rounded text-sm">
                  <div className="text-gray-600">
                    Caro cliente a primeira parcela do seu pedido vence em
                    janeiro. Ressaltamos que o valor total do material deverá
                    ser quitado dentro do ano letivo.
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Scheduled Orders */}
        {hasScheduledItems && (
          <Card className="mb-6 shadow-none border-dashed border-blue-300 bg-blue-50">
            <CardHeader className="bg-blue-100">
              <CardTitle className="text-lg text-blue-800">
                📋 Pedido programado 2º semestre
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-4">
                <div className="text-sm text-blue-700 mb-3">
                  As quantidades poderão ser ajustadas até {adjustmentDeadline}
                </div>

                <div className="flex justify-between items-center font-bold text-lg">
                  <span className="text-blue-800">
                    Total do Pedido Programado
                  </span>
                  <span className="text-blue-900">
                    {formatCurrency(totalValue)}
                  </span>
                </div>

                {brandGroups && brandGroups.length > 0 && (
                  <div className="space-y-3 mt-4">
                    {brandGroups.map((brandGroup, brandIndex) => (
                      <div
                        key={`scheduled-brand-${brandIndex}`}
                        className="space-y-2"
                      >
                        <div className="flex justify-between items-center py-2 px-3 bg-amber-100 rounded text-sm font-medium">
                          <span className="text-amber-700">
                            📚 {brandGroup.brandName}
                          </span>
                          <span className="text-amber-800 font-semibold">
                            {formatCurrency(brandGroup.subtotal)}
                          </span>
                        </div>
                        <div className="ml-3 space-y-1">
                          {brandGroup.products.map((item) => (
                            <div
                              key={item.id}
                              className="flex justify-between text-xs"
                            >
                              <span className="truncate flex-1">
                                {item.descricao_produto}
                                {item.utilizacao && (
                                  <span className="ml-2 text-gray-500">
                                    ({item.utilizacao})
                                  </span>
                                )}
                              </span>
                              <span className="ml-2 whitespace-nowrap text-gray-500">
                                Qtd. {item.quantidade_pedido}
                              </span>
                              <span className="ml-2 whitespace-nowrap">
                                {formatCurrency(item.valor_total)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4 p-3 bg-blue-100 rounded text-sm">
                  <div className="font-medium text-blue-800 mb-2">Resumo:</div>
                  <div className="text-blue-700">
                    Total de {totalQuantity} unidades do pedido programado 2º
                    semestre
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Note about detailed pages */}
        <Card className="mb-6 shadow-none border-gray-300">
          <CardContent className="pt-6">
            <div className="text-center text-sm text-gray-600 bg-blue-50 p-4 rounded-lg">
              <div className="font-semibold text-gray-800 mb-2">
                📋 Extrato Detalhado
              </div>
              <div>
                As próximas páginas contêm o detalhamento completo dos produtos
                <br />
                organizados por segmento e marca para sua conferência.
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t-2 border-gray-300 text-center text-sm text-gray-600">
          <div>
            Este documento foi gerado automaticamente pelo sistema de pedidos.
          </div>
          <div>Para dúvidas, entre em contato com nosso suporte.</div>
        </div>
      </div>

      {/* Detailed Pages - Second page onwards */}
      <div data-segment="detailed-pages">
        <PDFDetailedPages
          orderNumber={orderNumber}
          clientName={clientName}
          orderItems={orderItems}
        />
      </div>
    </>
  );
};

export default PDFSummary;
